import React, { useState } from "react";
import { Reveal, Overline } from "./Motion";
import { SmartImage } from "./SmartImage";
import { LOCATIONS } from "@/data/cafe";
import { MapPin, Clock, Navigation, Share2, Car, CircleCheck, Locate, Loader2 } from "lucide-react";
import { toast } from "sonner";

const distanceKm = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export default function Locations() {
  const [nearestIdx, setNearestIdx] = useState(null);
  const [locating, setLocating] = useState(false);
  const share = async (loc) => {
    const shareData = {
      title: loc.name,
      text: `${loc.name} — ${loc.address}`,
      url: loc.maps,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${loc.name} — ${loc.address}\n${loc.maps}`);
        toast.success("Location link copied to clipboard!");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(loc.maps);
        toast.success("Location link copied!");
      } catch { toast.error("Couldn't share. Please try again."); }
    }
  };

  return (
    <section id="locations" className="bg-[#F5F2EB] py-24 md:py-32" data-testid="locations-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Overline>Our Locations</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1514] mt-4 font-medium">
              Find us across <span className="italic text-[#1C7C54]">Delhi NCR</span>
            </h2>
          </div>
          <p className="font-body text-[#1A1514]/60 max-w-sm">Three cozy corners, one Teaavi promise. Drop by the outlet nearest you.</p>
        </div>

        <div className="mt-6">
          <button type="button" data-testid="find-nearest-btn" disabled={locating}
            onClick={() => {
              if (!navigator.geolocation) { toast.error("Geolocation not supported by this browser."); return; }
              setLocating(true);
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                  const withDist = LOCATIONS.map((l, i) => ({ i, d: distanceKm(me, { lat: l.lat, lng: l.lng }), name: l.name }));
                  withDist.sort((a, b) => a.d - b.d);
                  const best = withDist[0];
                  setNearestIdx(best.i);
                  toast.success(`Nearest: ${LOCATIONS[best.i].name} · ${best.d.toFixed(1)} km away`);
                  setLocating(false);
                  document.querySelector(`[data-testid="location-${best.i}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                },
                (err) => { setLocating(false); toast.error(err.code === 1 ? "Location permission denied." : "Couldn't get your location."); },
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }}
            className="inline-flex items-center gap-2 rounded-full bg-[#1C7C54] text-[#F5F2EB] px-5 py-2.5 font-accent text-xs uppercase tracking-[0.15em] hover:bg-[#14603F] transition-colors disabled:opacity-70">
            {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />}
            {locating ? "Finding..." : "Find Café Near You"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {LOCATIONS.map((loc, i) => (
            <Reveal key={loc.name} delay={i * 0.1}>
              <div data-testid={`location-${i}`}
                className={`group flex flex-col h-full border rounded-sm overflow-hidden transition-colors bg-white ${nearestIdx === i ? "border-[#1C7C54] ring-2 ring-[#1C7C54]/40" : "border-[#1A1514]/12 hover:border-[#1C7C54]"}`}>
                <a href={loc.maps} target="_blank" rel="noreferrer" className="relative overflow-hidden h-52 block" data-testid={`location-map-${i}`}>
                  <SmartImage src={loc.image} alt={loc.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1514]/60 to-transparent" />
                  <span className="absolute top-4 left-4 overline text-[#F5F2EB] bg-[#1C7C54] px-3 py-1 rounded-sm">{loc.city}</span>
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-[#F5F2EB]/95 text-[#1C7C54] px-2.5 py-1 rounded-full font-accent text-[10px] uppercase tracking-[0.1em]" data-testid={`location-availability-${i}`}>
                    <CircleCheck className="h-3 w-3" /> Open now
                  </span>
                </a>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl text-[#1A1514] group-hover:text-[#1C7C54] transition-colors">{loc.name}</h3>
                  <div className="mt-4 space-y-2">
                    <p className="flex items-start gap-2 font-body text-sm text-[#1A1514]/65">
                      <MapPin className="h-4 w-4 text-[#1C7C54] mt-0.5 shrink-0" strokeWidth={1.5} />{loc.address}
                    </p>
                    <p className="flex items-center gap-2 font-body text-sm text-[#1A1514]/65">
                      <Clock className="h-4 w-4 text-[#1C7C54] shrink-0" strokeWidth={1.5} />{loc.hours}
                    </p>
                    <p className="flex items-center gap-2 font-body text-sm text-[#1A1514]/65">
                      <Car className="h-4 w-4 text-[#1C7C54] shrink-0" strokeWidth={1.5} />Free parking* up to 60 min
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#1A1514]/10">
                    <a href={loc.maps} target="_blank" rel="noreferrer" data-testid={`location-directions-${i}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-sm bg-[#1C7C54] text-[#F5F2EB] px-3 py-2.5 font-accent text-[11px] uppercase tracking-[0.12em] hover:bg-[#14603F] transition-colors">
                      <Navigation className="h-3.5 w-3.5" /> Directions
                    </a>
                    <button type="button" onClick={() => share(loc)} data-testid={`location-share-${i}`}
                      aria-label={`Share ${loc.name}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-sm border border-[#1C7C54]/50 text-[#1C7C54] px-3 py-2.5 font-accent text-[11px] uppercase tracking-[0.12em] hover:bg-[#1C7C54] hover:text-[#F5F2EB] transition-colors">
                      <Share2 className="h-3.5 w-3.5" /> Share
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="font-body text-xs text-[#1A1514]/50 mt-8" data-testid="parking-footnote">
          * Free parking up to 60 minutes at all outlets — or complimentary on orders above ₹499. Subject to availability.
        </p>
      </div>
    </section>
  );
}
